import React from "react";
import {GameHealthResponse, GameTrackingHealthStatus, OWGame} from "@devour/client";
import useGetOverwolfGameInfo from "@/hooks/useGetOverwolfGameInfo";
import useGetOverwolfGameEventsDisabled from "@/hooks/useGetOverwolfGameEventsDisabled";
import useFilteredOverwolfGameInfo from "@/hooks/useFilteredOverwolfGameInfo";
import useGameHealth from "@/hooks/useGameHealth";
import classNames from "classnames";
import {displayGameTitle} from "@/utils/displayGameTitle";
import {
    overwolfGameInfo,
    calculateGameStatus,
    getAbbreviatedStatusText,
} from "@/hooks/useOverwolfInterop";

/**
 * Merge the client-side `calculateGameStatus` output with backend
 * GameTrackingHealthSnapshot status. Most-restrictive wins:
 *   - backend RED forces RED regardless of local state (server has
 *     authoritative outage signal the client can't see locally)
 *   - backend YELLOW upgrades local green → yellow
 *   - otherwise local status wins (offline / disabled signals from the
 *     OW client are still authoritative for "your machine can't track")
 *
 * Does NOT modify `calculateGameStatus` in useOverwolfInterop — the
 * local hook stays untouched per the unconditional CLAUDE.md "Do not
 * touch" rule on useOverwolfInterop; the merge happens here in the
 * chip-adjacent code instead.
 */
function mergeBackendHealthStatus(
    localStatus: "green" | "yellow" | "red" | "gray",
    backendHealth: GameHealthResponse | undefined,
): "green" | "yellow" | "red" | "gray" {
    if (!backendHealth) return localStatus;
    if (backendHealth.status === GameTrackingHealthStatus.RED) return "red";
    if (backendHealth.status === GameTrackingHealthStatus.YELLOW && localStatus === "green") return "yellow";
    return localStatus;
}

/**
 * Build a player-readable warning suffix when scoring is degraded.
 *
 * It does not name the game details, and the backend no longer sends them:
 * that list was ordered by highest event count, so it named the busiest —
 * working — details as the broken ones. The Help Center reads Overwolf's
 * per-detail status, the only source that knows what isn't reading.
 */
function buildBackendWarning(backendHealth: GameHealthResponse | undefined): string {
    if (!backendHealth) return "";
    if (backendHealth.status === GameTrackingHealthStatus.GREEN || backendHealth.status === GameTrackingHealthStatus.NO_DATA) {
        return "";
    }
    return backendHealth.status === GameTrackingHealthStatus.RED
        ? " (Tracking Currently Down)"
        : " (Some Events not Tracking)";
}

const getWarningMessage = (gameInfo: overwolfGameInfo, devourGameInfo: OWGame, gameEventsDisabled: boolean, gameEventState: number): string | undefined => {
    // game is running but gep registration failed
    if (gameInfo?.gepStarted === false && gameInfo?.isGameRunning) {
        return " (Issue detecting game events)";
    }
    if (devourGameInfo?.isEventSupported && gameEventsDisabled) {
        return ` (quests${gameEventState === 2 ? " may be " : " "}off)`;
    }
    return "";
};

interface OverwolfGameStatusChipProps {
    onClick?: () => void;
    compact?: boolean; // Use abbreviated text for mobile/compact views
    /*
     * Render the colored status dot ONLY — no text, no chrome. Used in the
     * in-game compact panel (~380px wide) where the full chip pushes the
     * avatar off-screen. Color encodes status the same as the full chip
     * (green active / yellow degraded / red offline / gray no-game). The
     * onClick wrapper still works so users can tap the dot to see details
     * via the existing handler.
     */
    iconOnly?: boolean;
}

const OverwolfGameStatusChip = React.memo<OverwolfGameStatusChipProps>(({ onClick, compact = false, iconOnly = false }) => {
    /*
     * Use the filtered gameInfo so the status chip stops flickering between Battle.net
     * and the real game when anti-cheat briefly revokes GEP's hook. See
     * useFilteredOverwolfGameInfo for launcher-suppression + stop-debounce rules.
     */
    const gameInfo = useFilteredOverwolfGameInfo();
    const { data: devourGameInfo } = useGetOverwolfGameInfo(gameInfo?.gameId);
    const { gameEventsDisabled, state} = useGetOverwolfGameEventsDisabled(gameInfo?.gameId);
    /*
     * Backend health check — uses the resolved OWGame's _id (MongoDB
     * ObjectId), not the OW integer gameId from the Redux gameInfo,
     * because the public health endpoint is keyed by ObjectId. Waits
     * for devourGameInfo to load; while it's loading, only local
     * status drives the chip (graceful degradation, no flash).
     */
    const { data: backendHealth } = useGameHealth(devourGameInfo?.id);

    // Enhance gameInfo with GEP status for our calculation
    const enhancedGameInfo = gameInfo ? {
        ...gameInfo,
        gameEventsDisabled,
        overwolfGameState: state,
        gepStatus: gameInfo.gepStarted && !gameEventsDisabled && state === 1 ? "healthy" as const
            : gameEventsDisabled || state === 0 || state === 3 ? "offline" as const
                : "degraded" as const,
    } : null;


    const localStatus = calculateGameStatus(enhancedGameInfo);
    const gameStatus = mergeBackendHealthStatus(localStatus, backendHealth);
    const localWarning = getWarningMessage(gameInfo, devourGameInfo, gameEventsDisabled, state);
    const backendWarning = buildBackendWarning(backendHealth);
    /*
     * Backend warning takes priority — it knows the game is degraded even when
     * the local client looks fine. Falls through to the local warning when
     * backend is green/no-data and local has something to say.
     */
    const warning = backendWarning || localWarning;

    // Determine display text
    const getDisplayText = (): string => {
        if (compact) {
            return getAbbreviatedStatusText(gameStatus);
        }

        if (!gameInfo) {
            return "No Game";
        }

        /*
         * Just the game title. The chip COLOR (green active / yellow degraded /
         * red offline / gray no-game) is the status signal — no "In " prefix and
         * no warning words on the chip face, which is what pushed the avatar
         * off-screen. The full warning detail lives in the hover tooltip below.
         */
        return displayGameTitle(gameInfo.gameTitle) || "Your game";
    };

    /*
     * Hover tooltip keeps the degraded/offline wording (e.g. "(some game
     * Events not Tracking)") available without rendering it on the chip.
     */
    const tooltipText = warning ? `${getDisplayText()}${warning}` : getDisplayText();

    if (iconOnly) {
        return (
            <div
                className={classNames("overwolf-govip_rewards_tag overwolf-govip_rewards_tag--icon-only", {
                    "overwolf-govip_rewards_tag_active": gameStatus === "green",
                    "overwolf-govip_rewards_tag_warning": gameStatus === "yellow",
                    "overwolf-govip_rewards_tag_down": gameStatus === "red",
                    "overwolf-govip_rewards_tag_gray": gameStatus === "gray",
                })}
                onClick={onClick}
                style={onClick ? { cursor: "pointer" } : undefined}
                role="status"
                aria-label={tooltipText}
                title={tooltipText}
            >
                <div
                    className={classNames("circle", {
                        circle_active: gameStatus === "green",
                        circle_warning: gameStatus === "yellow",
                        circle_down: gameStatus === "red",
                        circle_gray: gameStatus === "gray",
                    })}
                />
            </div>
        );
    }

    return (
        <div
            className={classNames("overwolf-govip_rewards_tag", {
                "overwolf-govip_rewards_tag_active": gameStatus === "green",
                "overwolf-govip_rewards_tag_warning": gameStatus === "yellow",
                "overwolf-govip_rewards_tag_down": gameStatus === "red",
                "overwolf-govip_rewards_tag_gray": gameStatus === "gray",
            })}
            onClick={onClick}
            style={onClick ? { cursor: "pointer" } : undefined}
            title={tooltipText}
        >
            <div
                className={classNames("circle", {
                    circle_active: gameStatus === "green",
                    circle_warning: gameStatus === "yellow",
                    circle_down: gameStatus === "red",
                    circle_gray: gameStatus === "gray",
                })}
            />
            {getDisplayText()}
        </div>
    );
});

OverwolfGameStatusChip.displayName = "OverwolfGameStatusChip";

export default OverwolfGameStatusChip;